import { ReactNode, useRef } from 'react';

import NavBar from 'components/Website/Navbar';
import TickerTapeBar from 'components/Website/TickerTapeBar';
import Footer from 'layouts/Footer';
import {
  PRICING_PAGE,
  OPC_PAGE,
  // AFFILIATE_PROGRAM_ARTICLE_PAGE,
  MASTER_CLASS_ARTICLE_PAGE,
  BLOG_INDEX_PAGE,
  TECHNICAL_ANALYSIS_LANDING_PAGE,
  OPTION_FLOW_PAGE,
  CONGRESS_TRADES_PAGE,
  INSIDER_TRADES_PAGE,
  EARNING_CALENDAR_PAGE_PAGE,
  NANCY_PELOSI_PAGE,
  NEWS_INDEX_PAGE,
  GEX_PAGE,
  MARKET_HEATMAP_PAGE,
  OI_PAGE,
  VOL_PAGE,
} from 'constants/pages';
import classNames from 'styles/utils/classNames';
import useMeasuredHeight from 'hooks/useMeasuredHeight';
import { IS_FREE_FLOW_ENABLED, IMPLIED_VOLATILITY_ENABLED } from 'constants/flags';
import TopBanner from 'components/TopBanner';
import {
  LegacyParityDealsLoader,
  useActiveParityOffer,
  useParityOffer,
} from 'context/ParityOfferContext';

import { PageLayoutStyled } from './styles';

/**
 * Marketing pages that pass no banner of their own still need the regional offer — the
 * ParityDeals script used to cover every page in this layout. Rendering TopBanner only when an
 * offer is active keeps that coverage without adding a site-wide sale bar to pages that
 * deliberately have none.
 */
function RegionalOfferBanner() {
  const parityOffer = useActiveParityOffer();
  if (!parityOffer) return null;

  return <TopBanner shouldShowOnScroll={false} />;
}

type LayoutProps = {
  children: ReactNode;
  fullwidth?: boolean;
  hideTopBar?: boolean;
  topBanner?: ReactNode;
  hasPadTop?: boolean;
  isNavBordered?: boolean;
};

const NAVIGATION_ITEMS = [
  {
    name: 'Product',
    href: '#',
    current: true,
    isMenu: true,
    subMenu: [
      {
        name: 'Options Flow',
        href: OPTION_FLOW_PAGE,
        current: true,
      },
      { name: 'Technical Analysis System', href: TECHNICAL_ANALYSIS_LANDING_PAGE, current: true },
      { name: 'Options Profit Calculator', href: OPC_PAGE, current: true },
      { name: 'Gamma Exposure', href: GEX_PAGE, current: true },
      { name: 'Open Interest Levels', href: OI_PAGE, current: true },
      ...(IMPLIED_VOLATILITY_ENABLED
        ? [{ name: 'Implied Volatility', href: VOL_PAGE, current: true }]
        : []),
      { name: 'S&P 500 Heatmap', href: MARKET_HEATMAP_PAGE, current: true },
      { name: 'Congress Trades', href: CONGRESS_TRADES_PAGE, current: true },
      { name: 'Nancy Pelosi Stock Trades', href: NANCY_PELOSI_PAGE, current: true },
      { name: 'Company Insider Trades', href: INSIDER_TRADES_PAGE, current: true },
      { name: 'Earnings Calendar', href: EARNING_CALENDAR_PAGE_PAGE, current: true },
      { name: 'Market-Moving Finance News', href: NEWS_INDEX_PAGE, current: true },
    ],
  },
  { name: 'Pricing', href: PRICING_PAGE, current: false },
  { name: 'Options Profit Calculator', href: OPC_PAGE, current: false },
  // { name: 'Affiliate Program', href: AFFILIATE_PROGRAM_ARTICLE_PAGE, current: false },
  {
    name: 'Free Education',
    href: '#',
    current: false,
    isMenu: true,
    subMenu: [
      { name: 'Free Masterclass', href: MASTER_CLASS_ARTICLE_PAGE, current: false },
      { name: 'Resource Center', href: BLOG_INDEX_PAGE, current: false },
    ],
  },
  ...(IS_FREE_FLOW_ENABLED
    ? [{ name: 'Options Flow', href: OPTION_FLOW_PAGE, current: false }]
    : []),
];

export default function PageLayout({
  children,
  hideTopBar,
  topBanner,
  fullwidth = false,
  hasPadTop,
  isNavBordered,
}: LayoutProps) {
  /**
   * The header stack below is `fixed`, and the spacer that reserves room for it is a hardcoded
   * 4.5rem/3.5rem sized for the NavBar alone. Adding the regional bar pushed the first heading
   * under the header on pages with no extra top padding (`/newsletter`, `/learn`). Pages that
   * supply their own `topBanner` also pass `hasPadTop`, whose 5rem already covers the bar, so
   * this deliberately measures only the slot the layout owns.
   */
  const offerBannerRef = useRef<HTMLDivElement>(null);
  const offerBannerHeight = useMeasuredHeight(offerBannerRef);
  // Scoped to the marketing layout, which is the only place the old script ever loaded.
  const { provider } = useParityOffer();

  return (
    <PageLayoutStyled>
      {provider === 'paritydeals' && <LegacyParityDealsLoader />}
      <div className="fixed left-0 top-0 z-30 w-full">
        {topBanner || (
          <div ref={offerBannerRef}>
            <RegionalOfferBanner />
          </div>
        )}
        {!hideTopBar && <TickerTapeBar />}
        <NavBar navitems={NAVIGATION_ITEMS} fullwidth={fullwidth} isNavBordered={isNavBordered} />
      </div>
      <div className="h-[4.5rem] md:h-14">&nbsp;</div>
      <div aria-hidden style={{ height: offerBannerHeight }} />
      <div className={classNames('flex-1', hasPadTop && 'pt-20')}>{children}</div>
      <Footer fullwidth={fullwidth} />
    </PageLayoutStyled>
  );
}
